import { dateFormat, isoDateFormat } from '@/constants'
import { stackGmapService } from '@/services'
import { Page } from '@/types/enums'
import { GmapRecord } from '@/types/gmapMixinType'
import { GmapRecordSetting, StackGmapIconSetting } from '@/types/StackGmapServiceType'
import { GenerateGmapRecordParam } from '@/types/utilTypes'
import axios from 'axios'
import dayjs from 'dayjs'
import sanitizeHtml from 'sanitize-html'

export const roundQuarter = (num: number):number => parseFloat((Math.round(num * 4) / 4).toFixed(2))

export function sessionGet(key: string):string {
    const stringValue = window.sessionStorage.getItem(key)

    if(!stringValue) return ''

    const value = JSON.parse(stringValue)
    const expirationDate = new Date(value.expirationDate)
    if (expirationDate > new Date()) {
        return value.value
    } else {
        window.sessionStorage.removeItem(key)
        return ''
    }
}

// add into session
export function sessionSet(key: string, value: unknown, expirationInMin = 10):void {
    const expirationDate = new Date(new Date().getTime() + (60000 * expirationInMin))
    const newValue = {
        value: value,
        expirationDate: expirationDate.toISOString()
    }
    window.sessionStorage.setItem(key, JSON.stringify(newValue))
}

export function shuffle(a: string[]):string[] {
    let j, x, i
    for (i = a.length - 1; i > 0; i--) {
        j = Math.floor(Math.random() * (i + 1))
        x = a[i]
        a[i] = a[j]
        a[j] = x
    }

    return a
}

export function getGmapAddress(location: {address: string, city:string, province: string, country: string, state?: string, propertyAddress?: string}): string {
    return  `${(location.address || location.propertyAddress || '').trim()}, ${(location.city || '').trim()}, ${(location.state || location.province || '').trim()}, ${location.country.trim()}`
}


export function generateGmapRecord(param: GenerateGmapRecordParam): GmapRecord {
    if(!param.page) {
        param.page = param.recordSetting.page
    }

    return {
        id: param.id,
        address: getGmapAddress(param.dynamicObj),
        header: param.dynamicObj[param.recordSetting.header],
        body: param.recordSetting.body?.map( rs => ({label: rs.alias, value: param.dynamicObj[rs.field] ?? ''})),
        ...(param.enableUrl && {url: {label: 'View Details', value: `/${param.page}/${param.id}`}}),
        iconFieldValue: param.dynamicObj[param.gmapIconSetting.iconField ?? ''],
        backgroundFieldValue: param.dynamicObj[param.gmapIconSetting.backgroundField ?? '']
    }

}

export function isCorrectDateFormat(date: string) : boolean {
    return dayjs(date, dateFormat, true).isValid()
}

export function toAppDateFormat(unknownDate: string): string {
    return unknownDate ? dayjs(unknownDate).format(dateFormat) : ''
}

export function toIsoDateFormat(unknownDate: string) : string {
    return  unknownDate && isCorrectDateFormat(unknownDate) ? dayjs(unknownDate).format(isoDateFormat) : ''
}


export async function getGmapSettings(page:Page) : Promise<{gmapIconSetting: StackGmapIconSetting, gmapRecordSetting: GmapRecordSetting}>  {

    const gmapSettingsServiceFactory: Record<string, { getGmapRecordSetting: () => Promise<GmapRecordSetting>, getIconSetting: () => Promise<StackGmapIconSetting>}>  = {
        [Page.PROPERTY] : {
            getGmapRecordSetting: stackGmapService.getGmapRecordSetting.bind(stackGmapService, Page.PROPERTY),
            getIconSetting: stackGmapService.getIconSetting.bind(stackGmapService, Page.PROPERTY)
        },
        [Page.MAP]: {
            getGmapRecordSetting: stackGmapService.getGmapRecordSetting.bind(stackGmapService, Page.MAP),
            getIconSetting: stackGmapService.getIconSetting.bind(stackGmapService, Page.MAP)
        },
        [Page.JOBS]: {
            getGmapRecordSetting: stackGmapService.getGmapRecordSetting.bind(stackGmapService, Page.JOBS),
            getIconSetting: stackGmapService.getIconSetting.bind(stackGmapService, Page.JOBS)
        },
        [Page.GLOBAL]: {
            getGmapRecordSetting: () => new Promise(()=>{return}),
            getIconSetting: () => new Promise(()=>{return})
        },
        [Page.PROPERTY_INFORMATION]: {
            getGmapRecordSetting: stackGmapService.getGmapRecordSetting.bind(stackGmapService, Page.PROPERTY),
            getIconSetting: stackGmapService.getIconSetting.bind(stackGmapService, Page.PROPERTY)
        },
        [Page.ACCOUNTING]: {
            getGmapRecordSetting: stackGmapService.getGmapRecordSetting.bind(stackGmapService, Page.ACCOUNTING),
            getIconSetting: stackGmapService.getIconSetting.bind(stackGmapService, Page.ACCOUNTING)
        }
    }


    const gmapSettingsService = gmapSettingsServiceFactory[page] ?? gmapSettingsServiceFactory[Page.GLOBAL]
    const result = {
        gmapIconSetting: await gmapSettingsService.getIconSetting(),
        gmapRecordSetting: null as any
    }
    if(page && gmapSettingsService) {
        result.gmapRecordSetting = await gmapSettingsService.getGmapRecordSetting()
    } else {
        throw Error('Page variable has not been set')
    }

    return result

}


export async function initGmaps(this: {gmapIconSetting: StackGmapIconSetting, gmapRecordSetting: GmapRecordSetting, isMapConfigured: boolean}, gmapOptions: { page: Page}) : Promise<void> {
    try{
        if(!gmapOptions?.page) {
            return
        }
        const gmapSettings = await getGmapSettings(gmapOptions.page)

        this.gmapIconSetting = gmapSettings.gmapIconSetting
        this.gmapRecordSetting = gmapSettings.gmapRecordSetting
        this.isMapConfigured  = true
    } catch (e) {
        this.isMapConfigured  = false
        throw e
    }
}


export function isFeatureAccessible(permissions: string[], userPermissions: Record<string, boolean>): boolean {
    if(!permissions.length) return true
    const isAccessible = permissions.find( permission => userPermissions[permission])
    return !!isAccessible
}

export function generateAPIUrl(): string {
    if(window.location.hostname == 'localhost') {
        return 'https://localhost:44321'
    } else {
        const pattern = /^((?:[\w-]+[.])?)[\w]+[.][\w]+$/
        const matched = pattern.exec(window.location.hostname)
        if(matched) {
            return matched[1] == '' ? `https://api.${matched[0]}`: `https://api-${matched[0]}`
        } else {
            throw new Error('Please let support know if this error occured')
        }
    }
}


export function highlight(haystack: string, needle: string) : string {
    needle = (needle ?? '').replace(/[*]/g, '').replace(/[.]/, '\\.').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(needle, 'ig')
    const applyHighlight = (v:string)=>`<span class="yellow lighten-3">${ v && sanitizeHtml(v)}</span>`
    return  needle && haystack ? haystack.replace(regex, applyHighlight) :  haystack && sanitizeHtml(haystack)
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function getAxiosErrors(err: any): Record<string, string>{
    const errors = {General: ''} as Record<string, string>
    if(axios.isAxiosError(err) && err?.response?.status == 400 && err.response.data && err.response.data.errors) {
        for (const [key, value] of Object.entries(err.response.data.errors)) {
            errors[key] = (value as string[]).join(' ')
        }
        
        if (!errors.General) {
            errors.General = 'There are errors that require your attention'
        }
    } else {
        errors.General = 'We\'re sorry, but we can\'t process your request at this time. Please try again later.'
    }
    return errors
}


export function generateUniqueId(): string {
    const timestamp = Date.now() // Unix timestamp in milliseconds
    const randomNum = Math.floor(Math.random() * 1000000) // Random number between 0 and 999999
    return `${timestamp}-${randomNum}`

}

export async function decryptAes(
    base64CipherText: string,
    keyBytes: Uint8Array,
    ivBytes: Uint8Array
): Promise<string> {
    // Decode Base64 to Uint8Array
    const cipherArray = Uint8Array.from(
        atob(base64CipherText),
        c => c.charCodeAt(0)
    )

    // Import the key
    const cryptoKey = await window.crypto.subtle.importKey(
        'raw',
        keyBytes as any,
        { name: 'AES-CBC' },
        false,
        ['decrypt']
    )

    // Decrypt
    const decryptedBuffer = await window.crypto.subtle.decrypt(
        {
            name: 'AES-CBC',
            iv: ivBytes as any
        },
        cryptoKey,
        cipherArray
    )

    // Convert decrypted ArrayBuffer to string
    return new TextDecoder().decode(decryptedBuffer)
}


export function formatCurrency(value: string, currency:string ): string {
            return value && currency ? `${value} ${currency}` : value
        }

export function formatLandArea(value: string, landUnit:string ): string {
            return value && landUnit? `${value} ${landUnit}` : value
}

export function toDashSeparated(str: string): string {
  return str
    .toLowerCase()                                   // Convert to lowercase
    .normalize('NFD')                                // Normalize accented characters
    .replace(/[^\p{L}\p{N}\s]/gu, '')               // Remove non-letter/number characters (Unicode safe)
    .trim()                                          // Remove leading/trailing spaces
    .replace(/\s+/g, '-')                           // Replace spaces with dashes
}


export function openContact(contact: { mode: string, url?: string, email?: string, cc?: string, subject?: string }) :void {
    if(contact.mode === 'url') {
        window.open(contact.url, '_blank')
    } else if(contact.mode === 'email') {
        const parts: string[] = []
        if(contact.cc) parts.push(`cc=${encodeURIComponent(contact.cc)}`)
        if(contact.subject) parts.push(`subject=${encodeURIComponent(contact.subject)}`)
        const params = parts.join('&')

        window.open(`mailto:${contact.email}${params ? `?${params}` : ''}`, '_blank')
    }
}